/**
 * Helper functions for date and time manipulation in interview request components
 */

/**
 * Gets the minimum date for calendar selection (tomorrow)
 * @returns Date object representing tomorrow
 */
export const getCalendarMinDate = (): Date => {
  const today = new Date();
  const oneDayFromToday = new Date();
  oneDayFromToday.setDate(today.getDate() + 1);
  return oneDayFromToday;
};

/**
 * Gets the maximum date for calendar selection (10 days from today)
 * @returns Date object representing 10 days from today
 */
export const getCalendarMaxDate = (): Date => {
  const today = new Date();
  const tenDaysFromToday = new Date();
  tenDaysFromToday.setDate(today.getDate() + 10);
  return tenDaysFromToday;
};

/**
 * Gets time picker constraints for a given date
 * @param date - The date to get constraints for
 * @returns Object with start and end of day times
 */
export const getTimePickerConstraints = (date: Date) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  return {
    start: startOfDay,
    end: endOfDay,
  };
};

/**
 * Preserves time values when the date changes
 * @param newDate - The new date to apply
 * @param currentStartTime - The current start time to preserve
 * @param currentEndTime - The current end time to preserve
 * @returns Object with updated start and end times
 */
const preserveTimeOnDateChange = (newDate: Date | null, currentStartTime: Date | null, currentEndTime: Date | null) => {
  if (!newDate) {
    return { start: null, end: null };
  }

  const result = { start: null as Date | null, end: null as Date | null };

  // Preserve start time with new date
  if (currentStartTime) {
    result.start = new Date(newDate);
    result.start.setHours(
      currentStartTime.getHours(),
      currentStartTime.getMinutes(),
      currentStartTime.getSeconds(),
      currentStartTime.getMilliseconds()
    );
  }

  // Preserve end time with new date
  if (currentEndTime) {
    result.end = new Date(newDate);
    result.end.setHours(
      currentEndTime.getHours(),
      currentEndTime.getMinutes(),
      currentEndTime.getSeconds(),
      currentEndTime.getMilliseconds()
    );
  }

  return result;
};

/**
 * Extracts time from a datetime and applies it to a base date
 * @param sourceDateTime - The datetime to extract time from
 * @param baseDate - The date to apply the time to
 * @returns New date with extracted time applied
 */
export const extractTimeToDate = (sourceDateTime: Date, baseDate: Date): Date => {
  const result = new Date(baseDate);
  result.setHours(
    sourceDateTime.getHours(),
    sourceDateTime.getMinutes(),
    sourceDateTime.getSeconds(),
    sourceDateTime.getMilliseconds()
  );
  return result;
};

export default preserveTimeOnDateChange;
